import type { Action } from 'redux'
import { defaultGetParams as getParams } from '../../signalr/helpers'
import type { InvokeDefinition, StreamDefinition } from '../../signalr/types'
import { getNextMessageId } from '../helpers'
import {
  type CancelOrderAction,
  type CancelOrdersAction,
  type FetchUserOrdersAction,
  type SubmitOrderAction,
  addOrUpdateOperatorOrders,
  addOrUpdateUserOrders,
  cancelOrderFailure,
  mapFakeTransactionId,
  submitOrderFailure
} from './actions'
import { createOrderFromResponse } from './helpers'
import { getOperatorOrders, getUserOrders } from './selectors'
import type { Order, OrderResponse } from './types'

interface UserState {
  users?: { selectedUser?: number }
}

interface OrderSliceRoot {
  order: import('./reducer').State
}

export const submitOrderDefinition: InvokeDefinition<
  [number, string, number, number, number, string, number],
  number,
  SubmitOrderAction,
  UserState
> = {
  method: 'HitOrLiftOrder',
  getParams: (state, action) => {
    const {
      orderId,
      size,
      custId,
      spotCrossSelection,
      listId = 0
    } = action.payload
    const selUser = state.users?.selectedUser ?? 0
    const messageId = getNextMessageId()
    return [
      selUser,
      orderId,
      size,
      custId,
      messageId,
      spotCrossSelection,
      listId
    ]
  },
  mapResponse: (orderTransactionId, action) =>
    mapFakeTransactionId(action.payload.transactionId, orderTransactionId),
  mapError: (err, action) => {
    const { orderId, transactionId, size } = action.payload
    const listId = action.payload.listId ?? 0
    return submitOrderFailure(orderId, transactionId, listId, err, size)
  }
}

export const cancelOrderDefinition: InvokeDefinition<
  [number, string],
  void,
  CancelOrderAction,
  UserState
> = {
  method: 'CancelOrder',
  getParams: (state, action) => [
    state.users?.selectedUser ?? 0,
    action.payload.orderId
  ],
  mapError: (err, action) => cancelOrderFailure(action.payload.orderId, err)
}

export const cancelOrdersDefinition: InvokeDefinition<
  [number, string[]],
  void,
  CancelOrdersAction,
  UserState
> = {
  method: 'CancelOrders',
  getParams: (state, action) => [
    state.users?.selectedUser ?? 0,
    action.payload.orderIds
  ]
}

const mapOrderResponses = (
  responses: OrderResponse[],
  pendingOrders: Order[]
) =>
  responses.map((response) => createOrderFromResponse(response, pendingOrders))

export const getUserOrdersDefinition: StreamDefinition<
  [number],
  OrderResponse[],
  FetchUserOrdersAction,
  UserState & OrderSliceRoot
> = {
  method: 'GetMyOrders',
  strategy: 'merge',
  getParams: (state) => [state.users?.selectedUser ?? 0],
  mapResponse: (responses, _action, state) => {
    const pendingOrders = getUserOrders(state!).filter(
      (f: Order) => f.id === ''
    )
    return addOrUpdateUserOrders(mapOrderResponses(responses, pendingOrders))
  },
  cancelOn: ['order.unsubscribeFetchUserOrders'],
  bufferMs: 1000,
  emitEmptyBatch: true
}

export const getOperatorOrdersDefinition: StreamDefinition<
  [],
  OrderResponse[],
  Action,
  OrderSliceRoot
> = {
  method: 'GetOperatorOrders',
  strategy: 'merge',
  getParams,
  mapResponse: (responses, _action, state) => {
    const pendingOrders = getOperatorOrders(state!).filter(
      (f: Order) => f.id === ''
    )
    return addOrUpdateOperatorOrders(
      mapOrderResponses(responses, pendingOrders)
    )
  },
  cancelOn: [],
  bufferMs: 1000,
  emitEmptyBatch: true
}
